import { useState, type FormEvent } from "react";
import { useHoldChange } from "@/api/queries";
import { useToast } from "@/app/toast-context";
import { Button } from "@/components/ui/controls";
import { Dialog } from "@/components/ui/Dialog";
import { HOLD_EFFECT, NOTE_MAX } from "./hold";
import styles from "./hold.module.css";

function problemOf(error: unknown): string {
  return error instanceof Error ? error.message : "That didn't work. Try again in a moment.";
}

/** Asks why, then holds every automatic fix in the workspace at once. */
export function HoldDialog({ onClose }: { onClose: () => void }) {
  const change = useHoldChange();
  const toast = useToast();
  const [note, setNote] = useState("");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await change.mutateAsync({ on: true, note: note.trim() || null });
    } catch {
      return; // the dialog says what went wrong
    }
    toast("Automatic fixes are on hold.", "ok");
    onClose();
  };

  return (
    <Dialog
      title="Hold all automatic fixes?"
      lead={`${HOLD_EFFECT} Checks and verification still run, and a built-in fix whose check fails still undoes its change. Everyone in the workspace sees why, and admins and approvers are told.`}
      onClose={onClose}
    >
      <form className={styles.form} onSubmit={(e) => void submit(e)}>
        <label className={styles.field}>
          <span className={styles.label}>Why</span>
          <textarea
            className={styles.textarea}
            rows={3}
            maxLength={NOTE_MAX}
            value={note}
            placeholder="What's wrong, so others know when to lift it"
            onChange={(e) => setNote(e.target.value)}
          />
          <span className={styles.hint}>Optional. Shown to everyone while the hold is on.</span>
        </label>
        {change.isError ? (
          <p className={styles.problem} role="alert">
            {problemOf(change.error)}
          </p>
        ) : null}
        <div className={styles.actions}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="danger" busy={change.isPending}>
            Hold now
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * Lifts the hold, after saying what that means. With `deployment`, ZEAOPS_HOLD_FIXES holds every
 * workspace as well, so lifting ends only the workspace's own hold.
 */
export function LiftDialog({
  deployment = false,
  onClose,
}: {
  deployment?: boolean;
  onClose: () => void;
}) {
  const change = useHoldChange();
  const toast = useToast();

  // Awaited rather than passed to mutate: lifting the hold takes the banner (and this dialog
  // with it) off the screen before mutate's own callbacks would run.
  const lift = async () => {
    let after;
    try {
      after = await change.mutateAsync({ on: false });
    } catch {
      return; // the dialog says what went wrong
    }
    toast(
      after.on
        ? "This workspace's hold is lifted. Fixes stay held for the whole deployment."
        : "Automatic fixes are running again.",
      "ok",
    );
    onClose();
  };

  return (
    <Dialog
      title="Lift the hold?"
      lead={
        deployment
          ? "This lifts the workspace's own hold. Fixes stay held for this whole deployment (ZEAOPS_HOLD_FIXES) until whoever runs ZeaOps turns that off."
          : "Fixes run on their own again where their policy allows. Fixes the hold stopped stay with the people they were handed to, and requests already waiting still wait for a person."
      }
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={change.isPending} onClick={() => void lift()}>
            Lift the hold
          </Button>
        </>
      }
    >
      {change.isError ? (
        <p className={styles.problem} role="alert">
          {problemOf(change.error)}
        </p>
      ) : (
        <p className={styles.hint}>Admins and approvers are told it's lifted.</p>
      )}
    </Dialog>
  );
}
